import { Box, debounce, Stack } from '@mui/material';
import Breadcrumbs from '../../components/Breadcrumb';
import { UserRole } from '../../types/user.types';
import { metaTitle } from '../../util/tabTitle';
import { useIsUserRole } from '../../util/useIsUserRole';
import { DevicesTable } from './DevicesTable';
import { AddDeviceModal } from './components/AddDeviceModal';
import { FC, useState } from 'react';
import { DEBOUNCE_TIME } from '../../configs';
import { useHandleSearch } from '../../util/useHandleSearch';
import { useLocationSearch } from '../../util/useLocationSearch';
import {
  ManagementPageHeader,
  ManagementSearch,
  ManagementToolbar,
  RefreshAction,
} from '../../components/ManagementPageLayout';

const DevicesPage: FC = () => {
  metaTitle('Devices');

  const searchVal = useLocationSearch();
  const [openUserModal, setOpenUserModal] = useState(false);
  const [refreshUserTable, setRefreshUserTable] = useState(false);
  const [search, setSearch] = useState(searchVal || '');
  const [searchText, setSearchText] = useState(searchVal || '');
  useHandleSearch(searchText);

  const disableAddUser = useIsUserRole([
    UserRole.user,
    UserRole.dataEditor,
    UserRole.layoutEditor,
  ]);

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, mx: 'auto' }}>
      <Breadcrumbs
        items={[
          {
            label: 'Devices',
            to: '/devices',
            description:
              'This page displays all registered devices (screens) within the system. Users can add a device by entering the code shown on the device into the designated space. To receive the code, the device must have the app installed. The device list shows the status of each device, indicating whether it is online or offline. The actions available include viewing device details, removing a device, and obtaining screenshots as proof of play. In the device view, users can access basic device information, the currently playing presentation, remove the device from the list, and set a scheduler for the device.',
          },
        ]}
      />
      <ManagementPageHeader
        title="Devices & screens"
        subtitle="Register, monitor, and manage every connected display."
        actionLabel="Add device"
        onAction={() => setOpenUserModal(true)}
        actionDisabled={disableAddUser}
      />
      <ManagementToolbar>
        <ManagementSearch
          placeholder="Search devices"
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
            debounce(() => setSearch(event.target.value), DEBOUNCE_TIME)();
          }}
        />
        <Stack direction="row" justifyContent="flex-end">
          <RefreshAction
            onClick={() => setRefreshUserTable(!refreshUserTable)}
          />
        </Stack>
      </ManagementToolbar>

      <DevicesTable refresh={refreshUserTable} search={search} />

      <AddDeviceModal
        onClose={() => setOpenUserModal(false)}
        open={openUserModal}
        onFinished={() => {
          setOpenUserModal(false);
          setRefreshUserTable(!refreshUserTable);
        }}
      />
    </Box>
  );
};

export default DevicesPage;
